<script setup lang="ts">
import type { BaseTooltipPlacement } from '@/js/account/components/base/BaseTooltip.vue';
import { ref } from 'vue';
import ActionButton from '@/js/account/components/base/ActionButton.vue';
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import BaseTitle from '@/js/account/components/base/BaseTitle.vue';
import BaseTooltip from '@/js/account/components/base/BaseTooltip.vue';
import {
  DOCUMENT_DOWNLOAD_FORMAT_LABELS,
  type DocumentDownloadFormat,
} from '@/js/account/entities/document';
import { classNames } from '@/js/lib/bem';

withDefaults(
  defineProps<{
    documentTitle: string
    formats: DocumentDownloadFormat[]
    disabled?: boolean
    placement?: BaseTooltipPlacement
  }>(),
  {
    disabled: false,
    placement: 'right-start',
  },
);

const emit = defineEmits<{
  download: [format: DocumentDownloadFormat]
}>();

const cn = classNames('download-format-menu');
const isOpen = ref(false);

function onFormatSelect(format: DocumentDownloadFormat): void {
  emit('download', format);
}
</script>

<template>
  <BaseTooltip
    v-if="formats.length > 0"
    v-model:open="isOpen"
    :class="cn()"
    interaction="click"
    theme="popover"
    :placement="placement"
    :disabled="disabled"
  >
    <div :class="cn('trigger', { open: isOpen })">
      <ActionButton
        icon="download"
        :label="`Download ${documentTitle}`"
        :disabled="disabled"
        :class="cn('trigger-button')"
        :aria-expanded="isOpen"
        aria-haspopup="menu"
      />
    </div>

    <template #content>
      <div :class="cn('menu')" role="menu" aria-label="Download as">
        <BaseTitle size="sm" :class="cn('title')">
          Download as
        </BaseTitle>

        <div :class="cn('actions')">
          <BaseButton
            v-for="format in formats"
            :key="format"
            v-close-popper
            theme="primary"
            size="sm"
            full-width
            role="menuitem"
            :class="cn('action')"
            @click="onFormatSelect(format)"
          >
            {{ DOCUMENT_DOWNLOAD_FORMAT_LABELS[format] }}
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseTooltip>
</template>

<style lang="scss" scoped>
.download-format-menu {
  display: inline-flex;

  &__trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    color: var(--color-black);
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
      background-color: var(--color-background-primary);
    }

    &_open {
      background-color: var(--color-background-primary);
    }
  }

  &__menu {
    box-sizing: border-box;
    width: 220px;
    padding: 16px;
    background-color: var(--color-white);
    border-radius: 4px;
  }

  &__title {
    margin: 0 0 12px;
    color: var(--color-text-primary);
    font-family: var(--font-family-serif);
    line-height: 1.2;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__action {
    width: 100%;
    max-width: none;
  }
}
</style>
